/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppDispatch } from '../../store/store';

type StopFunction = (...args:any[]) => boolean;
type UniqueFunction = {
  [key: string]: ReturnType<typeof setTimeout>
};
type ActiveFlags = {
  [key: string]: boolean
};

/**
 * Higher-order function that adds long polling capability to an API reducer.
 *
 * This utility manages repeated dispatches of an API action at a specified interval,
 * and automatically stops polling when a user-defined stop condition is met.
 * It ensures that only one polling operation per unique `thunkName` is active at a time.
 *
 * @param apiReducer - The thunk or API reducer function to be dispatched repeatedly.
 * @param interval - The polling interval in milliseconds.
 * @param stopFunction - A function that receives the API response and returns `true` to stop polling, or `false` to continue.
 * @returns A function that starts the polling process for a given `thunkName`, and dispatches a `nextReducer` with `nextPayload` when polling stops.
 *
 * @example
 * const poller = withLongPolling(fetchDataThunk, 2000, (data) => data.finished);
 * poller('fetchData', finishReducer, true, dispatch);
 */
export const withLongPolling = (apiReducer:any, interval:number, stopFunction:StopFunction) => {
  const uniqueFunctions: UniqueFunction = {};
  const activeFlags: ActiveFlags = {};

  function longPoll(thunkName:string, nextReducer:any, nextPayload:boolean, apiDispatcher:AppDispatch) {
    // istanbul ignore next
    if (!activeFlags[thunkName]) return; // Prevent race: stop if not active

    apiDispatcher(apiReducer())
      .then((data: any) => {
        // Check if the stop condition is met using the provided stopFunction
        if (stopFunction(data)) {
          // If stop condition is met, clear the polling timeout
          clearTimeout(uniqueFunctions[thunkName]);
          // Remove the reference to the timeout for cleanup
          delete uniqueFunctions[thunkName];
          // Mark this polling instance as inactive
          activeFlags[thunkName] = false;
          // Dispatch the next reducer with the provided payload
          apiDispatcher(nextReducer(nextPayload));
        } else {
          // If stop condition is not met, clear any existing timeout
          clearTimeout(uniqueFunctions[thunkName]);
          // Schedule the next poll after the specified interval
          // istanbul ignore next
          uniqueFunctions[thunkName] = setTimeout(() => longPoll(thunkName, nextReducer, nextPayload, apiDispatcher), interval);
        }
      }).catch(() => {
        clearTimeout(uniqueFunctions[thunkName]);
        // istanbul ignore next
        uniqueFunctions[thunkName] = setTimeout(() => longPoll(thunkName, nextReducer, nextPayload, apiDispatcher), interval);
      });
  }

  /**
   * Ensures that a long polling function for a specific thunk is only scheduled once at a time.
   *
   * This function checks if a polling operation for the given `thunkName` is already active.
   * If not, it sets a flag and schedules the `longPoll` function to be called after a specified interval.
   * The scheduled timeout is stored in the `uniqueFunctions` map to prevent duplicate scheduling.
   *
   * @param thunkName - The unique identifier for the thunk or polling operation.
   * @param nextReducer - The reducer function or value to be passed to the long polling function.
   * @param nextPayload - A boolean payload or flag to be passed to the long polling function.
   * @param apiDispatcher - The dispatcher used to trigger API actions.
   * @returns The timeout identifier for the scheduled polling operation.
   */
  function getUniqueFunction(thunkName: string, nextReducer:any, nextPayload:boolean, apiDispatcher:AppDispatch) {
    if (!activeFlags[thunkName] && !uniqueFunctions[thunkName]) {
      activeFlags[thunkName] = true;
      uniqueFunctions[thunkName] = setTimeout(() => longPoll(thunkName, nextReducer, nextPayload, apiDispatcher), interval);
    }
    return uniqueFunctions[thunkName];
  }

  // eslint-disable-next-line func-names
  return function (thunkName:string, nextReducer:any, nextPayload: boolean, apiDispatcher:AppDispatch) {
    getUniqueFunction(thunkName, nextReducer, nextPayload, apiDispatcher);
  };
};
